import {fetch} from "./api-wrapper";
import Utils from "./utils";
import {ADMIN_SECTION_DEFINITIONS} from "./admin-sections";
export default class AccessControl {
    // --- helpers ---
    static _norm(name) {
        if (!name) return "";
        const n = String(name).trim();
        return n === "data" || n === "data " ? "data" : n;
    }

    static _roleCfg(data, roleName="") {
        const name = roleName || data?.role;
        const roles = data?.roles_config || [];
        const exactMatch = roles.find(r => r?.name === name);
        if (exactMatch) return exactMatch;
        const normalizedName = String(name || "").trim().toLowerCase();
        return roles.find(r => String(r?.name || "").trim().toLowerCase() === normalizedName) || null;
    }

    static _permNames(roleCfg) {
        return (roleCfg?.permitted_modules || []).map(m => this._norm(m?.name)).filter(Boolean);
    }

    static _optionName(option) {
        return typeof option === "string" ? option : option?.name;
    }
    
    static userHasLimitedAccess(data) {
        data = JSON.parse(JSON.stringify(data))
        const rc = this._roleCfg?.(data) || data;
        return rc?.permitted_modules?.limited_access === true && rc?.permitted_modules?.modules?.length > 0;
    }
    
    static getLimitedModule(data) {
        data = JSON.parse(JSON.stringify(data))
        const rc = this._roleCfg?.(data) || data;
        const limited_access = rc?.permitted_modules?.limited_access === true && rc?.permitted_modules?.modules?.length > 0;
        const permitted_modules = rc?.permitted_modules?.modules?.map(m => this._norm(m?.name)).filter(Boolean) || [];

        if (limited_access) return permitted_modules[0] || null;
        return null;
    }

    static _moduleLandingPath(moduleName) {
        const module = this._norm(moduleName);
        const landingByModule = {
            expenses: "/expenses_accounts",
            payments: "/payments/invoices",
        };

        return landingByModule[module] || null;
    }

    static getDefaultLandingPage(data, fallbackPage = null) {
        const module = this.getLimitedModule(data);
        if (module) {
            return this._moduleLandingPath(module);
        }

        return fallbackPage;
    }

    static redirectToDefaultLanding(data, navigate, fallbackPage = null) {
        const target = this.getDefaultLandingPage(data, fallbackPage);
        if (target) {
            navigate(target);
        }
    }
    static navigateToLimitedPage(data, navigate, fallbackPage = null) {
        this.redirectToDefaultLanding(data, navigate, fallbackPage);
    }
    static userHasPageAccess(data, page) {
        const component_is_activated = Utils.userHasPageAccess(data, page);
        const target = this._norm(page);

        data = JSON.parse(JSON.stringify(data))
        const rc = this._roleCfg?.(data) || data;
        const limited_access = rc?.permitted_modules?.limited_access === true && rc?.permitted_modules?.modules?.length > 0;
        const permitted_modules = rc?.permitted_modules?.modules?.map(m => this._norm(m?.name)) || [];
        return component_is_activated && (!limited_access || permitted_modules.includes(target));

    }

    static userHasPageOptionAccess(data, moduleName, optionName) {
        // Option access is defined by tenant module options, then constrained by role module access
        if (!this.userHasPageAccess(data, moduleName)) return false;
        const mod = (data?.config?.components || []).find(c => this._norm(c?.name) === this._norm(moduleName));
        if (!((mod?.options || []).find(o => this._norm(this._optionName(o)) === this._norm(optionName)))) return false;

        if (this._norm(moduleName) !== "expenses") return true;

        const roleViews = this._roleCfg(data)?.view_configuration?.expenses?.views;
        if (!Array.isArray(roleViews)) return true;

        return roleViews.map(view => this._norm(view)).includes(this._norm(optionName));
    }

    static userHasRole(data, roles) {
        return !!(data && roles?.includes?.(data.role));
    }

    static get_role_names(data) {
        data = JSON.parse(JSON.stringify(data))
        const roles_config = (data && data.roles_config) || [];
        return roles_config.map(r => r.name).filter(Boolean);
    }

    static hasModuleAccess(data, moduleName) {
        data = JSON.parse(JSON.stringify(data))
        return this.userHasPageAccess(data, moduleName);
    }

    static hasActionAccess(data, actionName) {
        data = JSON.parse(JSON.stringify(data))
        const rc = this._roleCfg(data);
        if (!rc) return false;
        // Array.isArray, not `|| []`: role documents are stored unvalidated, so
        // authorized_actions can be a bare string. `.includes` on a string is
        // substring matching, which granted "canValidateExpense" to a role holding
        // the single string "canValidateExpense" — while the server, which
        // iterates the value into characters, refused it. Rendered button, 401
        // behind it. XPEN-3377's backfill patch skips these documents for the same
        // reason, so the shape is reachable. XPEN-3379.
        const actions = rc.authorized_actions;
        return Array.isArray(actions) && actions.includes(actionName);
    }

    static canAccessAdminShell(data) {
        return this.userHasRole(data, ["SuperAdmin", "Admin"]) ||
            this.canAccessAnyAdminSection(data);
    }

    static canAccessAdminSection(data, sectionKey) {
        const section = ADMIN_SECTION_DEFINITIONS.find(({key}) => key === sectionKey);
        if (!section) return false;
        if (section.module && !Utils.userHasPageAccess(data, section.module)) return false;
        return this.userHasRole(data, ["SuperAdmin", "Admin"]) ||
            this.hasActionAccess(data, section.action);
    }

    static canAccessAnyAdminSection(data) {
        return ADMIN_SECTION_DEFINITIONS.some(({key}) => this.canAccessAdminSection(data, key));
    }

    static getFirstAdminSectionPath(data, fallback = "/dashboard") {
        return ADMIN_SECTION_DEFINITIONS.find(({key}) => this.canAccessAdminSection(data, key))?.path || fallback;
    }

    static canAccessVendorFingerprintMonitoring(data) {
        return this.userHasPageAccess(data, "monitor") &&
            this.hasActionAccess(data, "canAccessVendorFingerprintMonitoring");
    }

    static hasUniversalProxy(data) {
        data = JSON.parse(JSON.stringify(data))
        const rc = this._roleCfg(data);
        return Boolean(rc?.universalProxy);
    }

    static canAccessApprovals(data) {
        return this.canReviewApprovals(data) ||
            this.hasActionAccess(data, "canAccessApprovals") ||
            this.hasActionAccess(data, "canRequestApproval");
    }

    static canReviewApprovals(data) {
        return this.userHasRole(data, ["SuperAdmin", "Admin", "Approver"]) ||
            this.hasActionAccess(data, "canApproveOrDeny");
    }

    static canAccessReports(data) {
        return this.userHasRole(data, ["SuperAdmin", "Admin", "Manager", "Approver"]) ||
            this.hasActionAccess(data, "canAccessReports");
    }

    static canAccessExpenseConfigurations(data) {
        return Boolean(this.userHasPageAccess(data, "expenses")) &&
            (this.userHasRole(data, ["SuperAdmin", "Admin", "Manager"]) ||
                this.hasActionAccess(data, "canAccessExpenseConfigurations"));
    }

    static canAccessExpenseRules(data) {
        return Boolean(this.userHasPageAccess(data, "expenses")) &&
            (this.userHasRole(data, ["SuperAdmin", "Admin", "Manager"]) ||
                this.hasActionAccess(data, "canAccessExpenseRules"));
    }

    /**
     * Whether this user may validate expense lines. The stored
     * `canValidateExpense` permission is the only grant (XPEN-3379).
     *
     * `view_configuration.expenses.views` is honoured only as a *restriction*. The
     * role editor pre-fills that list with every enabled view the moment "Limit
     * expense views for this role" is ticked, so reading its presence as a grant
     * would hand validation to any role an administrator merely narrowed. The
     * server does not enforce that half at all — only the action.
     *
     * Do not reintroduce a role-name branch. The server's `has_action_access` does
     * not special-case Admin or SuperAdmin, so a role name that grants here and
     * not there renders a button with a 401 behind it.
     *
     * A visibility gate, not a security boundary: the server applies the same
     * action check to PUT /expenses/validate/<account_id>/<checksum>.
     *
     * @param {object} data - the MobX store: role, roles_config and config.components.
     * @returns {boolean} true only when this user may validate expense lines.
     */
    static userCanValidateExpenses(data) {
        return Boolean(this.userHasPageOptionAccess(data, "expenses", "controller")) &&
            this.hasActionAccess(data, "canValidateExpense");
    }

    /**
     * Whether this user may correct an expense amount once its report is Approved or
     * Validated (XPEN-3786). Like userCanValidateExpenses, the stored action is the only
     * grant: the server's has_action_access has no Admin bypass. A visibility gate; the
     * server refuses the save with 403 expense_correction_forbidden.
     */
    static userCanCorrectValidatedExpenses(data) {
        return this.hasActionAccess(data, "canCorrectValidatedExpense");
    }

    /**
     * Whether this user may send a submitted report back to its employee (XPEN-3795).
     * The stored action is the only grant, as for userCanCorrectValidatedExpenses.
     */
    static userCanReturnExpenseReports(data) {
        return this.hasActionAccess(data, "canReturnExpenseReport");
    }

    static canCreateCreditCardInvoice(data) {
        return Boolean(this.userHasPageAccess(data, "c-card")) &&
            (this.userHasRole(data, ["SuperAdmin", "Admin", "Manager"]) ||
                this.hasActionAccess(data, "canCreateCreditCardInvoice"));
    }

    static canSeeInvoiceStatusFilter(data) {
        return this.userHasRole(data, ["SuperAdmin", "Admin", "Manager"]) ||
            this.hasActionAccess(data, "canSeeInvoiceStatusFilter");
    }

    // Views are simple key/value flags under view_configuration
    static getViewValue(data, key, fallback = null) {
        data = JSON.parse(JSON.stringify(data))
        const rc = this._roleCfg?.(data) || data;
        const cfg = rc?.view_configuration || {};
        if (!key) return fallback;

        const parts = key.split(".");
        let value = cfg;

        for (const part of parts) {
            if (value == null || typeof value !== "object" || !(part in value)) {
                return fallback;
            }
            value = value[part];
        }

        return value === undefined ? fallback : value;
    }

    static hasViewAccess(data, key, expectedValue = true) {
        data = JSON.parse(JSON.stringify(data))
        const val = this.getViewValue(data, key, undefined);
        if (expectedValue === undefined) return val !== undefined;
        return val === expectedValue;
    }

    static userCanModify(data, user) {
        const DEFAULT_ROLES =   ["SuperAdmin", "Admin", "Manager", "Approver", "Clerk", "Assistant"]
        const roleHierarchy = {
            SuperAdmin: ["SuperAdmin", "Admin", "Manager", "Approver", "Clerk", "Assistant"],
            Admin: ["Admin", "Manager", "Approver", "Clerk", "Assistant"],
            Manager: ["Manager", "Approver", "Clerk", "Assistant"],
            Approver: ["Approver"],
            Clerk: ["Clerk"],
            Assistant: ["Assistant"]
        };

        return roleHierarchy[data.role].includes(user.role) || !DEFAULT_ROLES.includes(user.role);
    }
    // convenience getters
    static listAuthorizedActions(data) {
        data = JSON.parse(JSON.stringify(data))
        const rc = this._roleCfg(data);
        return (rc?.authorized_actions || []).slice();
    }

    static listPermittedModules(data) {
        data = JSON.parse(JSON.stringify(data))
        const rc = this._roleCfg(data);
        return this._permNames(rc);
    }

    static get_all_roles_names() {
        const url = `/roles/names/`;

        const uniq = (arr) => Array.from(new Set(arr));
        const clean = (s) => String(s || "").trim();

        return new Promise((resolve, reject) => {
            fetch(
                url,
                "GET",
                {},
                (response) => {
                    const role_names = Array.isArray(response?.data) ? response.data : [];
                    resolve(role_names);
                },
                (err) => {
                    console.error("Failed to fetch roles:", err);
                    resolve([]); 
                }
            );
        });
    }


}
